// Lib
import { combineReducers } from "redux";
import { configureStore } from "@reduxjs/toolkit";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
// Api
import { ordersAPI } from "rtkQuery/query/ordersAPI";
import { authMeAPI } from "rtkQuery/query/authMeAPI";
import { permissionsAPI } from "rtkQuery/query/permissionsAPI";
import { customersPaymentAPI } from "rtkQuery/query/customersPaymentAPI";
import { transactionsAPI } from "rtkQuery/query/transactionsAPI";
import { customersAPI } from "rtkQuery/query/customersAPI";
import { exchangeRateAPI } from "rtkQuery/query/exchangeRateAPI";
import { balanceExpirationAPI } from "rtkQuery/query/balanceExpirationAPI";
import { settingsAPI } from "rtkQuery/query/settingsAPI";
import { authSettingsAPI } from "rtkQuery/query/authSettingsAPI";
import { coreSettingsAPI } from "rtkQuery/query/coreSettingsAPI";
import { coinPackagesApi } from "rtkQuery/query/coinPackagesApi";
import { rolesAPI } from "rtkQuery/query/rolesAPI";
import { reportsAPI } from "rtkQuery/query/reportsAPI";
import { usersAPI } from "rtkQuery/query/usersAPI";
import { locationsAPI } from "rtkQuery/query/locationsAPI";
import { recommendedAPI } from "rtkQuery/query/recommendedAPI";
import { timeSlotsAPI } from "rtkQuery/query/timeSlotsAPI";
import { cashPaymentsSettingsAPI } from "rtkQuery/query/cashPaymentsSettingsAPI";
import { referralsApi } from "rtkQuery/query/referralsAPI";
import { couponsAPI } from "rtkQuery/query/couponsAPI";
import { reviewsApi } from "rtkQuery/query/reviewsAPI";
import { loyaltyAPI } from "rtkQuery/query/loyaltyAPI";
import { mediaAPI } from "rtkQuery/query/mediaAPI";
import { notificationsSettingsAPI } from "rtkQuery/query/notificationsSettingsAPI";
import { foodLibraryAPI } from "rtkQuery/query/foodLibraryAPI";
import { healthyPerkAPI } from "rtkQuery/query/healthyPerkAPI";
import { uploadMediaAPI } from "rtkQuery/query/uploadMediaAPI";

// Selectors
import {
  authSliceReducer,
  uiSliceReducer,
  userSliceReducer,
  tableFiltersSliceReducer,
} from "rtkQuery/slices";
// Types
import { AuthState } from "types/auth";
import { UiState } from "types/ui";
import { userState } from "types/users";
import { TableFiltersState } from "types/tableFilters";
// Utils
import { token } from "utils/handleToken";
import { logsAPI } from "rtkQuery/query/logsAPI";

const LOGOUT_ACTIONS = ["auth/userLogout"];

const reducers = combineReducers({
  auth: authSliceReducer,
  user: userSliceReducer,
  ui: uiSliceReducer,
  tableFilters: tableFiltersSliceReducer,
  [ordersAPI.reducerPath]: ordersAPI.reducer,
  [permissionsAPI.reducerPath]: permissionsAPI.reducer,
  [authMeAPI.reducerPath]: authMeAPI.reducer,
  [customersAPI.reducerPath]: customersAPI.reducer,
  [reportsAPI.reducerPath]: reportsAPI.reducer,
  [notificationsSettingsAPI.reducerPath]: notificationsSettingsAPI.reducer,
  [customersPaymentAPI.reducerPath]: customersPaymentAPI.reducer,
  [transactionsAPI.reducerPath]: transactionsAPI.reducer,
  [exchangeRateAPI.reducerPath]: exchangeRateAPI.reducer,
  [balanceExpirationAPI.reducerPath]: balanceExpirationAPI.reducer,
  [settingsAPI.reducerPath]: settingsAPI.reducer,
  [authSettingsAPI.reducerPath]: authSettingsAPI.reducer,
  [coreSettingsAPI.reducerPath]: coreSettingsAPI.reducer,
  [coinPackagesApi.reducerPath]: coinPackagesApi.reducer,
  [rolesAPI.reducerPath]: rolesAPI.reducer,
  [usersAPI.reducerPath]: usersAPI.reducer,
  [locationsAPI.reducerPath]: locationsAPI.reducer,
  [recommendedAPI.reducerPath]: recommendedAPI.reducer,
  [timeSlotsAPI.reducerPath]: timeSlotsAPI.reducer,
  [cashPaymentsSettingsAPI.reducerPath]: cashPaymentsSettingsAPI.reducer,
  [referralsApi.reducerPath]: referralsApi.reducer,
  [couponsAPI.reducerPath]: couponsAPI.reducer,
  [reviewsApi.reducerPath]: reviewsApi.reducer,
  [loyaltyAPI.reducerPath]: loyaltyAPI.reducer,
  [healthyPerkAPI.reducerPath]: healthyPerkAPI.reducer,
  [foodLibraryAPI.reducerPath]: foodLibraryAPI.reducer,
  [uploadMediaAPI.reducerPath]: uploadMediaAPI.reducer,
  [mediaAPI.reducerPath]: mediaAPI.reducer,
  [logsAPI.reducerPath]: logsAPI.reducer,
});

const persistConfig = {
  key: "root",
  storage,
};

const rootReducer = (state, action) => {
  if (LOGOUT_ACTIONS.includes(action.type)) {
    state = undefined;
    token.clear();
  }

  return reducers(state, action);
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(
      ordersAPI.middleware,
      permissionsAPI.middleware,
      authMeAPI.middleware,
      customersAPI.middleware,
      reportsAPI.middleware,
      customersPaymentAPI.middleware,
      transactionsAPI.middleware,
      exchangeRateAPI.middleware,
      balanceExpirationAPI.middleware,
      settingsAPI.middleware,
      authSettingsAPI.middleware,
      coreSettingsAPI.middleware,
      uploadMediaAPI.middleware,
      mediaAPI.middleware,
      coinPackagesApi.middleware,
      rolesAPI.middleware,
      usersAPI.middleware,
      locationsAPI.middleware,
      recommendedAPI.middleware,
      timeSlotsAPI.middleware,
      cashPaymentsSettingsAPI.middleware,
      notificationsSettingsAPI.middleware,
      referralsApi.middleware,
      couponsAPI.middleware,
      reviewsApi.middleware,
      loyaltyAPI.middleware,
      foodLibraryAPI.middleware,
      healthyPerkAPI.middleware,
      logsAPI.middleware,
    ),
});

export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;

export type RootState = {
  auth: AuthState;
  ui: UiState;
  user: userState;
  tableFilters: TableFiltersState;
};
